import React, { useEffect, useMemo, useState } from "react";
import { useQuery, keepPreviousData } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import { useForm } from "react-hook-form";
import { useSelector } from "react-redux";
import { RootState } from "../../../store";

// components
import { GenericTableHeader } from "@surgia/ui";
import Button from "@surgia/ui/components/button";
import TableControls from "@surgia/ui/containers/table/table-controls";
import GenericModal from "@surgia/ui/components/genericModal";
import Input from "../../../components/input";
import { FiltersModal } from "../../Users";
//containers
import UsersTable from "../../../containers/tables/users-table";

// queries
import { usersQueries } from "@surgia/api/src/queryStore/shared/users";
import { getLocalStorageItem } from "../../Catalog";

// constants
const ITEMS_PER_PAGE = 5;

const userGroups = getLocalStorageItem("user_groups", []);
const branches = getLocalStorageItem("branches", []);

//icons
import inviteIcon from "@surgia/ui/icons/users/inviite.svg";
import mailIcon from "@surgia/ui/icons/popup/email.svg";

//hooks
import { useSendInvitation } from "../../../hooks/useSendInvitation";

// Invite User Form Component
export const InviteUserForm: React.FC<{
  onSuccess: (data: any) => void;
  onCancel: () => void;
  showBranches?: boolean;
  selectedBranchId?: number;
}> = ({ onSuccess, onCancel, showBranches = true, selectedBranchId }) => {
  const [selectedRoles, setSelectedRoles] = useState<number[]>([]);
  const [selectedBranches, setSelectedBranches] = useState<number[]>(
    selectedBranchId ? [selectedBranchId] : []
  );
  const [showRoleDropdown, setShowRoleDropdown] = useState(false);
  const [showBranchDropdown, setShowBranchDropdown] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitted },
  } = useForm({
    defaultValues: {
      firstName: "",
      lastName: "",
      phone: "",
      email: "",
      role: "",
      branch: "",
      mobile_number_prefix: "+966",
    },
  });

  const onSubmit = (data: any) => {
    onSuccess({
      email: data.email,
      role: selectedRoles,
      branch: selectedBranches,
    });
  };

  // Handle role selection
  const handleRoleToggle = (roleId: number) => {
    setSelectedRoles((prev) =>
      prev.includes(roleId)
        ? prev.filter((id) => id !== roleId)
        : [...prev, roleId]
    );
  };

  // Handle branch selection
  const handleBranchToggle = (branchId: number) => {
    setSelectedBranches((prev) =>
      prev.includes(branchId)
        ? prev.filter((id) => id !== branchId)
        : [...prev, branchId]
    );
  };

  return (
    <form className="space-y-4" onSubmit={handleSubmit(onSubmit)}>
      {/* Email address */}
      <Input
        label="Email address"
        placeholder="Enter user email"
        type="email"
        icon={mailIcon}
        iconAlt="Mail"
        register={register("email", {
          required: "Email is required",
          pattern: {
            value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
            message: "Invalid email address",
          },
        })}
        error={errors.email}
        isSubmitted={isSubmitted}
        required
      />

      {/* Role - Custom Multi-Select Dropdown */}
      <div>
        <label className="block mb-2 text-sm font-medium text-gray-700">
          Role <span className="text-blue-600">*</span>
        </label>
        <div className="relative">
          <button
            type="button"
            onClick={() => setShowRoleDropdown(!showRoleDropdown)}
            className={`w-full h-11 px-3.5 py-2.5 bg-white border border-[#D5D7DA] rounded-lg shadow-xs focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-left flex items-center justify-between ${
              selectedRoles.length > 0 ? "text-black" : "text-[#717680]"
            }`}
          >
            <span className="truncate flex-1 mr-2">
              {selectedRoles.length > 0
                ? selectedRoles
                    .map((id) => userGroups.find((g: any) => g.id === id)?.name)
                    .join(", ")
                : "Select user role"}
            </span>
            <svg
              className={`w-4 h-4 transition-transform flex-shrink-0 ${
                showRoleDropdown ? "rotate-180" : ""
              }`}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M19 9l-7 7-7-7"
              />
            </svg>
          </button>
        </div>
        {isSubmitted && selectedRoles.length === 0 && (
          <p className="text-red-500 text-xs mt-1">
            Please select at least one role
          </p>
        )}
      </div>

      {/* Branch - Custom Multi-Select Dropdown */}
      {showBranches && (
        <div>
          <label className="block mb-2 text-sm font-medium text-gray-700">
            Branch <span className="text-blue-600">*</span>
          </label>
          <div className="relative">
            <button
              type="button"
              onClick={() => setShowBranchDropdown(!showBranchDropdown)}
              className={`w-full h-11 px-3.5 py-2.5 bg-white border border-[#D5D7DA] rounded-lg shadow-xs focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-left flex items-center justify-between ${
                selectedBranches.length > 0 ? "text-black" : "text-[#717680]"
              }`}
            >
              <span className="truncate flex-1 mr-2 max-w-[200px]">
                {selectedBranches.length > 0
                  ? selectedBranches
                      .map(() => branches.find((b: any) => b.id)?.name)
                      .join(", ")
                  : "Select Branch"}
              </span>
              <svg
                className={`w-4 h-4 transition-transform flex-shrink-0 ${
                  showBranchDropdown ? "rotate-180" : ""
                }`}
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M19 9l-7 7-7-7"
                />
              </svg>
            </button>
          </div>
          {isSubmitted && selectedBranches.length === 0 && (
            <p className="text-red-500 text-xs mt-1">
              Please select at least one branch
            </p>
          )}
        </div>
      )}

      {/* Role Dropdown */}
      {showRoleDropdown && (
        <div className="absolute z-50 bg-white border border-[#D5D7DA] rounded-lg shadow-lg max-h-48 overflow-y-auto w-full">
          {userGroups.map((group: any) => (
            <div
              key={group.id}
              onClick={() => handleRoleToggle(group.id)}
              className={`px-3 py-2 cursor-pointer hover:bg-gray-50 ${
                selectedRoles.includes(group.id) ? "bg-blue-50" : ""
              }`}
            >
              <span className="text-sm text-gray-700">{group.name}</span>
            </div>
          ))}
        </div>
      )}

      {/* Branch Dropdown */}
      {showBranchDropdown && (
        <div className="absolute z-50 bg-white border border-[#D5D7DA] rounded-lg shadow-lg max-h-48 overflow-y-auto w-full">
          {branches.map((branch: any) => (
            <div
              key={branch.id}
              onClick={() => handleBranchToggle(branch.id)}
              className={`px-3 py-2 cursor-pointer hover:bg-gray-50 ${
                selectedBranches.includes(branch.id) ? "bg-blue-50" : ""
              }`}
            >
              <span className="text-sm text-gray-700">
                {branch.name ?? "No_name"}
              </span>
            </div>
          ))}
        </div>
      )}

      {/* Form buttons */}
      <div className="flex justify-between gap-4 w-full">
        <Button
          variant="outline"
          onClick={onCancel}
          className="w-full"
          type="button"
        >
          Cancel
        </Button>
        <Button variant="primary" className="w-full" type="submit">
          Send invitation
        </Button>
      </div>
    </form>
  );
};

// Main Users Tab Component
function Users() {
  const { t } = useTranslation();
  const { id: branchId } = useParams<{ id: string }>();
  const { sendInvitation } = useSendInvitation();
  const authState = useSelector((state: RootState) => state.auth);
  const tenant_id_from_store = authState.globalState.tenant_id;

  const TARGET_COMPONENT_ID =
    import.meta.env.VITE_VITEAUTH_URL?.includes("test") ||
    import.meta.env.VITE_VITEAUTH_URL?.includes("develop")
      ? 8
      : 6;

  // State management
  const [currentPage, setCurrentPage] = useState(0);
  const [openFilters, setOpenFilters] = useState(false);
  const [status, setStatus] = useState<string | undefined>("");
  const [search, setSearch] = useState("");
  const [showInviteModal, setShowInviteModal] = useState(false);
  // Updated filters state to match main users page
  const [filters, setFilters] = useState({
    name: "",
    id: "",
    tenant_id: 697,
    branches_ids: branchId || "", // Use the current branch ID
    user_group_ids: "",
    city_id: "",
    user_group_id: "", // Add user_group_id for filtering
  });

  // Add sorting state with initial sort by ID descending (newest first)
  const [sortState, setSortState] = useState<{
    column: string | null;
    direction: "asc" | "desc" | null;
  }>({
    column: "userId", // Initial sort column
    direction: "desc", // Initial sort direction (desc for newest first)
  });

  const statusTabs = [
    { label: "All", value: "" },
    { label: t("shared.tableToolbar.active"), value: "ACTIVE" },
    { label: t("shared.tableToolbar.pending"), value: "PENDING" },
  ];

  const { data: userGroupsResponse } = useQuery({
    ...usersQueries.queries.getUserGroups({
      component_id: import.meta.env.VITE_PUBLIC_COMPONENT_ID,
      tenant_id: tenant_id_from_store!,
      target_component_id: TARGET_COMPONENT_ID,
      target_tenant_id: tenant_id_from_store!,
    }),
  });

  const userGroupsData = userGroupsResponse?.data || [];

  // Fetch users data for this specific branch with sorting and user group filter
  const { data: usersResponse, isLoading: isUsersLoading } = useQuery({
    ...usersQueries.queries.getUsers({
      component_id:
        import.meta.env.VITE_KEYCLOAK_ISSUER?.includes("test") ||
        import.meta.env.VITE_KEYCLOAK_ISSUER?.includes("develop")
          ? 8
          : 6,
      name: search,
      tenant_id: tenant_id_from_store!,
      branches_ids: branchId || "",
      id: filters.id ?? "",
      profile_status: status !== "" && status ? status : "",
      size: ITEMS_PER_PAGE,
      page: currentPage,
      city_id: filters.city_id || "",
      userGroup_id: filters.user_group_id || "",
      sort:
        sortState.column && sortState.direction
          ? `${sortState.column},${sortState.direction}`
          : undefined,
    }),
    placeholderData: keepPreviousData,
    select: (data) => {
      return {
        total: data.total,
        data: data.data.map((user: any) => ({
          ...user,
          id: user.user_id,
          name: user.full_name,
          details: {
            email: user.email,
            phone: user.mobile,
          },
          role: user.user_groups,
          status: user.status,
          createdAt: user.creation_date,
          lastLogin: user.last_login,
          branches: user.user_branches,
        })),
      };
    },
  });

  // Computed values
  const totalCount = useMemo(() => {
    return usersResponse?.total || 0;
  }, [usersResponse?.total]);

  const onSubmit = (data: any) => {
    sendInvitation({
      tenant_id: tenant_id_from_store!,
      email: data.email,
      user_group_ids_list: [parseInt(data.role)],
      branch_ids_list: [parseInt(branchId || "0")], // Use current branch ID
      component_id: import.meta.env.VITE_PUBLIC_COMPONENT_ID,
    });

    setShowInviteModal(false);
  };

  useEffect(() => {
    setCurrentPage(0);
  }, [search, filters]);

  // Handle sort changes
  const handleSort = (newSortState: {
    column: string | null;
    direction: "asc" | "desc" | null;
  }) => {
    setSortState(newSortState);
    setCurrentPage(0);
  };

  return (
    <div className="bg-white rounded-xl border border-gray-200 shadow pt-4">
      <GenericTableHeader
        title="Users"
        totalCount={totalCount}
        status={status}
        singularLabel="user"
        pluralLabel="users"
        padding="px-4 py-4"
      />

      <TableControls
        status={status}
        setStatus={setStatus}
        search={search}
        setSearch={setSearch}
        openFilters={openFilters}
        setOpenFilters={setOpenFilters}
        statusTabs={statusTabs}
        showFilter={true}
      />

      {/* Users Table Section */}
      <UsersTable
        data={usersResponse?.data || []}
        currentPage={currentPage}
        totalPages={Math.ceil(totalCount / ITEMS_PER_PAGE)}
        onPageChange={setCurrentPage}
        loading={isUsersLoading}
        showSelectedBranchColumn={false} // Since we're in a specific branch, don't show branch column
        sortState={sortState}
        onSort={handleSort}
      />

      {/* Invite User Modal */}
      <GenericModal
        title="Invite new user"
        subtitle="Add a new user to this branch and assign their role."
        open={showInviteModal}
        onClose={() => setShowInviteModal(false)}
        titleIcon={<img src={inviteIcon} alt="Invite" className="w-10 h-10" />}
        additionalTitleStyles="!mb-0 text-[16px]"
        additionalSubtitleStyles="!text-[14px]"
        footer={<></>}
      >
        <InviteUserForm
          onSuccess={onSubmit}
          onCancel={() => setShowInviteModal(false)}
          showBranches={false} // Don't show branch selection since we're in a specific branch
          selectedBranchId={parseInt(branchId || "0")}
        />
      </GenericModal>

      {openFilters && (
        <FiltersModal
          open={openFilters}
          onClose={() => setOpenFilters(false)}
          onApply={() => setOpenFilters(false)}
          filters={filters}
          setFilters={setFilters}
          cities={branches}
          branches={[]}
          userGroups={userGroupsData}
          showCities={false}
        />
      )}
    </div>
  );
}

export default Users;
